import { useState } from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getNodeToggleState } from '../../utils/permissionTreeUtils';
import useProfileSx from '../../hooks/useProfileSx';
import PermissionNodeToggle from './PermissionNodeToggle';
import PermissionMatrixRow from './PermissionMatrixRow';
import { PERMISSION_MATRIX_COLUMNS } from '../../utils/rbacConstants';

function matrixGridTemplate(columnCount) {
  return `minmax(220px, 1.5fr) repeat(${columnCount}, minmax(92px, 1fr))`;
}

export default function PermissionModuleSection({
  section,
  grants,
  disabled = false,
  defaultExpanded = true,
  onNodeGrantChange,
  onFeatureChange,
  onObjectChange,
}) {
  const sx = useProfileSx();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const columns = PERMISSION_MATRIX_COLUMNS;
  const gridTemplateColumns = matrixGridTemplate(columns.length);
  const rootToggle = getNodeToggleState(section.rootNode, grants);

  const handleRootToggle = (granted) => {
    onNodeGrantChange?.(section.rootNode, granted);
    if (granted) {
      setExpanded(true);
    }
  };

  return (
    <Box sx={sx.permissionModuleSection}>
      <Box sx={{ ...sx.permissionModuleHeaderRow, gridTemplateColumns }}>
        <Box sx={sx.permissionModuleTitleCell}>
          <IconButton
            size="small"
            aria-label={expanded ? 'Collapse module' : 'Expand module'}
            onClick={() => setExpanded((prev) => !prev)}
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle1" sx={sx.permissionModuleTitle}>
            {section.label}
          </Typography>
        </Box>

        <Box sx={sx.permissionModuleViewCell}>
          {!rootToggle.isEmpty ? (
            <PermissionNodeToggle
              checked={rootToggle.checked}
              indeterminate={rootToggle.indeterminate}
              disabled={disabled}
              onChange={handleRootToggle}
            />
          ) : (
            <Typography variant="caption" sx={sx.permissionMatrixColumnHeader}>
              View
            </Typography>
          )}
        </Box>

        {columns.slice(1).map((column) => (
          <Typography key={column.key} variant="caption" sx={sx.permissionMatrixColumnHeader}>
            {column.label}
          </Typography>
        ))}
      </Box>

      <Collapse in={expanded}>
        <Box sx={sx.permissionMatrixScroll}>
          {section.groups.map((group) => {
            const groupToggle = getNodeToggleState(group.node, grants);
            const rowCount = group.featureRows.length + group.objectRows.length;
            const showGroupHeader =
              section.groups.length > 1
              || (rowCount > 1 && group.label !== section.label);

            return (
              <Box key={group.codename} sx={sx.permissionModuleGroup}>
                {showGroupHeader ? (
                  <Box sx={{ ...sx.permissionModuleGroupHeader, gridTemplateColumns }}>
                    <Typography variant="body2" sx={sx.permissionModuleGroupTitle}>
                      {group.label}
                    </Typography>
                    <Box sx={sx.permissionModuleViewCell}>
                      {!groupToggle.isEmpty ? (
                        <PermissionNodeToggle
                          checked={groupToggle.checked}
                          indeterminate={groupToggle.indeterminate}
                          disabled={disabled}
                          onChange={(granted) => onNodeGrantChange?.(group.node, granted)}
                        />
                      ) : null}
                    </Box>
                    {columns.slice(1).map((column) => (
                      <Box key={column.key} />
                    ))}
                  </Box>
                ) : null}

                {group.objectRows.map((row) => (
                  <PermissionMatrixRow
                    key={row.id}
                    row={row}
                    grants={grants}
                    columns={columns}
                    gridTemplateColumns={gridTemplateColumns}
                    disabled={disabled}
                    onFeatureChange={onFeatureChange}
                    onObjectChange={onObjectChange}
                  />
                ))}

                {group.featureRows.map((row) => (
                  <PermissionMatrixRow
                    key={row.id}
                    row={row}
                    grants={grants}
                    columns={columns}
                    gridTemplateColumns={gridTemplateColumns}
                    disabled={disabled}
                    onFeatureChange={onFeatureChange}
                    onObjectChange={onObjectChange}
                  />
                ))}
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}
